import {
  CourseMappingAttributes,
  CourseMappingChildRow,
  CourseMappingGroup,
  CourseMappingOffering,
} from '../interfaces/CourseMappingInterface';
import { CourseSearchAttributes, CourseSearchCourse, CourseSearchRow } from '../interfaces/CoursesInterface';

const mapAttributes = (attributes: CourseSearchAttributes | null): CourseMappingAttributes => ({
  number_of_credits: attributes?.number_of_credits ?? null,
  is_ib: attributes?.is_ib ?? null,
  is_ap: attributes?.is_ap ?? null,
  is_aice: attributes?.is_aice ?? null,
  is_honors: attributes?.is_honors ?? null,
  is_dual_enrollment: attributes?.is_dual_enrollment ?? null,
  is_college_preparatory: attributes?.is_college_preparatory ?? null,
  is_cte: attributes?.is_cte ?? null,
  language_category: attributes?.language_category ?? null,
  language_level: attributes?.language_level ?? null,
  is_lab_science: attributes?.is_lab_science ?? null,
  subject_area: attributes?.subject_area
    ? {
        name: attributes.subject_area.name ?? null,
        subcategory: attributes.subject_area.subcategory ?? null,
      }
    : null,
});

const buildGroupId = (courseIds: string[]) => [...courseIds].sort().join(':');

const compareCourses = (left: CourseSearchCourse, right: CourseSearchCourse) => {
  const schoolCompare = left.school.localeCompare(right.school, undefined, { sensitivity: 'base' });
  if (schoolCompare !== 0) {
    return schoolCompare;
  }

  const periodCompare = left.academic_period.localeCompare(right.academic_period, undefined, { numeric: true });
  if (periodCompare !== 0) {
    return periodCompare;
  }

  return left.id.localeCompare(right.id);
};

// Each visible row maps to exactly one real course, so edits can target a single course_id.
const buildGroupOfferings = (
  groupId: string,
  courses: CourseSearchCourse[]
): { parentOffering: CourseMappingOffering; children: CourseMappingChildRow[] } => {
  const sortedCourses = [...courses].sort(compareCourses);

  if (sortedCourses.length === 0) {
    return {
      parentOffering: { id: `${groupId}-0`, courseId: '', schoolName: ' ', academicPeriod: ' ' },
      children: [],
    };
  }

  const [parent, ...rest] = sortedCourses;

  return {
    parentOffering: {
      id: parent.id,
      courseId: parent.id,
      schoolName: parent.school || ' ',
      academicPeriod: parent.academic_period || ' ',
    },
    children: rest.map((course) => ({
      id: course.id,
      courseId: course.id,
      schoolName: course.school || ' ',
      academicPeriod: course.academic_period || ' ',
    })),
  };
};

const mapCourseSearchRow = (row: CourseSearchRow): CourseMappingGroup => {
  // `courses` is the single source of truth for the variant's courses, so group.courseIds always
  // matches the per-row course ids the rest of the pipeline keys edits on.
  const courses = row.courses ?? [];
  const courseIds = courses.map((course) => course.id);
  const groupId = buildGroupId(courseIds);
  const attributes = mapAttributes(row.attributes);
  const { parentOffering, children } = buildGroupOfferings(groupId, courses);

  return {
    id: groupId,
    courseIds,
    name: row.name ?? ' ',
    code: row.code ?? ' ',
    attributes,
    parentOffering,
    children,
  };
};

export const mapCourseSearchResultsToGroups = (rows: CourseSearchRow[]): CourseMappingGroup[] =>
  rows.map(mapCourseSearchRow);
